import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import _ from 'lodash';
import React from 'react';

require('highcharts/modules/exporting')(Highcharts);

interface ITrendChartComponentProps {
  title: string
  isLoading: boolean
  legend: boolean
  categories: string[]
  factor: string
  seriesName: string
  series: any[]
  chartHeight?: number
  htmlSubtitle?: string
  avannisAvg: string | number
  dataPoints: any[]
}

interface ITrendChartComponentState {
  trendChartRef: any
  trendGraphOptions: any
  dataPoints: any[]
  categories: string[]
}

export class AreaChartComponent extends React.Component<ITrendChartComponentProps> {
  state: ITrendChartComponentState = {
    trendChartRef: null,
    dataPoints: [],
    trendGraphOptions: null,
    categories: [],
  }
  componentDidMount() {
    this.state.trendChartRef = React.createRef();

    if (this.props.dataPoints.length > 0) {
      this.setTrendGraphOptions();
    }
  }

  componentDidUpdate(prevProps: ITrendChartComponentProps, prevState: ITrendChartComponentState) {
    if (prevState.dataPoints !== this.state.dataPoints) {
      this.setTrendGraphOptions();
    }

    if (prevProps.dataPoints !== this.props.dataPoints) {
      this.setTrendGraphOptions();
    }
  }

  setCategories() {
    const { series } = this.props;
    if (series.length > 0) {
      this.setState({
        categories: series.reduce((acc, curr) => {
          return acc.concat(curr.display)
        }, [])
      })
    }
  }

  setTrendGraphOptions() {
    const { seriesName, title, legend, factor, chartHeight, htmlSubtitle, avannisAvg, categories, dataPoints } = this.props;
    let height = 300;
    if (chartHeight && chartHeight > 0) {
      height = chartHeight;
    }

    let min = _.min(dataPoints.reduce((acc, curr) => acc.concat(curr.y), []));
    let max = _.max(dataPoints.reduce((acc, curr) => acc.concat(curr.y), []));

    const suffix = factor === '100' ? '%' : '';

    let subtitle = {};
    if (htmlSubtitle) {
      subtitle = {
        useHTML: true,
        text: htmlSubtitle
      };
    }

    let exportingOptions = {};
    let navigationOptions = {};

    if (!categories) {
      exportingOptions = {
        buttons: {
          contextButton: {
            enabled: false
          }
        }
      };
      navigationOptions = {
        buttonOptions: {
          enabled: true
        }
      };
    } else {
      exportingOptions = {
        chartOptions: {
          title: {
            style: { display: 'block' },
          },
          plotOptions: {
            series: {
              dataLabels: {
                enabled: true,
                format: '{y}' + suffix
              }
            }
          },
          subtitle: {
            useHTML: true,
            text: htmlSubtitle
          }
        },
        buttons: {
          contextButton: {
            symbol: 'url(images/bars.png)',
          }
        }
      };
      navigationOptions = {
        buttonOptions: {
          verticalAlign: 'top',
          y: -10
        }
      };
    }

    // add plot line for Avannis values reference
    let pl = {};

    if (avannisAvg !== 0 && avannisAvg < min) {
      min = avannisAvg;
    }

    if (avannisAvg !== 0 && max < avannisAvg) {
      max = avannisAvg;
    }
    if (max > 90 && factor == '100') {
      max = 105;
    }

    if (avannisAvg !== 0) {
      pl = {
        value: avannisAvg,
        color: '#FFAA00',
        width: 3,
        zIndex: 2,
        label: {
          text: avannisAvg + suffix,
          align: 'right',
          style: {
            fontWeight: 'bold',
          }
        }
      };
    }

    const seriesArr = [{
      name: seriesName,
      fillColor: {
        linearGradient: [0, 0, 0, 300],
        stops: [
          [0, 'rgba(160,216,239,0.3)'],
          [1, 'rgba(255,255,255,0.3)']
        ]
      },
      data: dataPoints,
      color: '#0493FC'
    }];
    this.setState({
      trendGraphOptions: {
        lang: {
          noData: 'Please contact support to measure' // todo: AVR-39 add open support ticket modal to this component
        },
        chart: {
          type: 'area',
          height,
        },
        title: {
          useHTML: true,
          text: title,
          x: 0,
          y: 5,
          align: 'left',
          style: { display: 'none' },
        },
        subtitle,
        legend: {
          enabled: legend,
        },
        xAxis: {
          categories,
        },
        tooltip: {
          borderWidth: 0,
          borderRadius: 0,
          shadow: false,
          useHTML: true,
          backgroundColor: 'rgba(255,255,255,0)',

          formatter() {
            return `<div class="tooltip-for-highchart" style="padding: 5px;">${this.x}<br/><span class="tooltip-interviews">${this.point.y}${suffix}</span><br/>Surveys: ${this.point.interviews}</div>`;
          }
        },
        yAxis: {
          title: null,
          labels: {
            enabled: true
          },
          min,
          max,
          plotLines: [pl],
          maxPadding: .45
        },
        plotOptions: {
          line: {
            dataLabels: {
              enabled: true,
              format: '{point.y:,.0f}' + suffix
            }
          }
        },
        credits: {
          enabled: false
        },
        series: seriesArr,
        exporting: exportingOptions,
        navigation: navigationOptions
      }
    });
  }

  setDataPoints(series: any[]) {
    this.setState({ dataPoints: series });
  }

  render() {
    const { trendGraphOptions, trendChartRef } = this.state;

    return (
      <div>
        <section className="highcharts-component">
          {
            (trendGraphOptions && trendChartRef) &&
            <HighchartsReact
              highcharts={Highcharts}
              options={trendGraphOptions}
              allowChartUpdate={true}
              ref={trendChartRef}
            />
          }
        </section>
      </div>
    );
  }
}
